import { useState, useEffect } from 'react';
import { useNavigation } from '@refinedev/core';
import { useParams } from 'react-router-dom';
import {
  Card,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Select,
  Button,
  Space,
  Descriptions,
  Tag,
  Row,
  Col,
  message,
  Spin,
} from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { apiClient } from '../../lib/api-client';

interface BusTrip {
  id: number;
  busCompany: { id: number; name: string };
  departureRegion: { nameUz: string };
  departureCity: { nameUz: string };
  arrivalRegion: { nameUz: string };
  arrivalCity: { nameUz: string };
  departureBusStation: { nameUz: string } | string | null;
  arrivalBusStation: { nameUz: string } | string | null;
  departureTime: string;
  arrivalTime: string;
  pricePerSeat: number;
  totalSeats: number;
  availableSeats: number;
  busType: string;
  busNumber: string | null;
  status: string;
  description: string | null;
  amenities: string[];
  createdAt: string;
}

const statusLabels: Record<string, string> = {
  SCHEDULED: 'Rejalashtirilgan',
  DEPARTED: "Yo'lda",
  COMPLETED: 'Yakunlangan',
  CANCELLED: 'Bekor qilingan',
};

const statusColors: Record<string, string> = {
  SCHEDULED: 'blue',
  DEPARTED: 'orange',
  COMPLETED: 'green',
  CANCELLED: 'red',
};

// Valid status transitions
const statusTransitions: Record<string, string[]> = {
  SCHEDULED: ['DEPARTED', 'CANCELLED'],
  DEPARTED: ['COMPLETED', 'CANCELLED'],
  COMPLETED: [],
  CANCELLED: [],
};

const getStationName = (station: { nameUz: string } | string | null | undefined): string => {
  if (!station) return '-';
  if (typeof station === 'string') return station;
  return station.nameUz || '-';
};

export const BusTripEdit = () => {
  const { id } = useParams();
  const { show, list } = useNavigation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [trip, setTrip] = useState<BusTrip | null>(null);

  useEffect(() => {
    const fetchTrip = async () => {
      setLoading(true);
      try {
        const response = await apiClient.get(`/admin/bus/trips/${id}`);
        const data = response.data.data || response.data;
        setTrip(data);
        form.setFieldsValue({
          departureTime: data.departureTime ? dayjs(data.departureTime) : null,
          arrivalTime: data.arrivalTime ? dayjs(data.arrivalTime) : null,
          pricePerSeat: data.pricePerSeat,
          totalSeats: data.totalSeats,
          busNumber: data.busNumber,
          description: data.description,
          status: data.status,
        });
      } catch {
        message.error("Reys ma'lumotlarini yuklashda xatolik");
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchTrip();
  }, [id]);

  const handleSave = async (values: any) => {
    setSaving(true);
    try {
      const payload: any = {
        pricePerSeat: values.pricePerSeat,
        totalSeats: values.totalSeats,
        busNumber: values.busNumber || null,
        description: values.description || null,
      };

      if (values.departureTime) {
        payload.departureTime = values.departureTime.toISOString();
      }
      if (values.arrivalTime) {
        payload.arrivalTime = values.arrivalTime.toISOString();
      }
      if (values.status && values.status !== trip?.status) {
        payload.status = values.status;
      }

      await apiClient.put(`/admin/bus/trips/${id}`, payload);
      message.success('Reys muvaffaqiyatli yangilandi');
      show('admin/bus/trips', Number(id));
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Xatolik yuz berdi');
    } finally {
      setSaving(false);
    }
  };

  const allowedStatuses = trip?.status
    ? [trip.status, ...(statusTransitions[trip.status] || [])]
    : [];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!trip) {
    return (
      <Card>
        <p>Reys topilmadi</p>
        <Button onClick={() => list('admin/bus/trips')}>Orqaga</Button>
      </Card>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0 }}>Reysni tahrirlash #{trip.id}</h2>
        <Space>
          <Button onClick={() => show('admin/bus/trips', Number(id))}>
            Bekor qilish
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            onClick={() => form.submit()}
          >
            Saqlash
          </Button>
        </Space>
      </div>

      <Row gutter={24}>
        <Col xs={24} lg={8}>
          <Card title="Reys ma'lumotlari (o'zgartirilmaydi)">
            <Descriptions column={1} size="small">
              <Descriptions.Item label="ID">{trip.id}</Descriptions.Item>
              <Descriptions.Item label="Kompaniya">
                {trip.busCompany?.name}
              </Descriptions.Item>
              <Descriptions.Item label="Yo'nalish">
                {trip.departureCity?.nameUz} → {trip.arrivalCity?.nameUz}
              </Descriptions.Item>
              <Descriptions.Item label="Viloyat">
                {trip.departureRegion?.nameUz} → {trip.arrivalRegion?.nameUz}
              </Descriptions.Item>
              <Descriptions.Item label="Jo'nash bekati">
                {getStationName(trip.departureBusStation)}
              </Descriptions.Item>
              <Descriptions.Item label="Yetib borish bekati">
                {getStationName(trip.arrivalBusStation)}
              </Descriptions.Item>
              <Descriptions.Item label="Avtobus turi">
                <Tag color={trip.busType === 'VIP' ? 'gold' : 'default'}>
                  {trip.busType}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Yaratilgan">
                {new Date(trip.createdAt).toLocaleDateString('uz-UZ')}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        <Col xs={24} lg={16}>
          <Card title="Tahrirlash">
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSave}
            >
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Jo'nash vaqti"
                    name="departureTime"
                    rules={[{ required: true, message: "Jo'nash vaqtini tanlang" }]}
                  >
                    <DatePicker
                      showTime
                      style={{ width: '100%' }}
                      format="YYYY-MM-DD HH:mm"
                      placeholder="Sana va vaqtni tanlang"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Yetib borish vaqti"
                    name="arrivalTime"
                    rules={[{ required: true, message: 'Yetib borish vaqtini tanlang' }]}
                  >
                    <DatePicker
                      showTime
                      style={{ width: '100%' }}
                      format="YYYY-MM-DD HH:mm"
                      placeholder="Sana va vaqtni tanlang"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Narx (so'm)"
                    name="pricePerSeat"
                    rules={[{ required: true, message: 'Narxni kiriting' }]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      formatter={(value) =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
                      }
                      parser={(value) => value!.replace(/\s/g, '') as unknown as 0}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Jami o'rindiqlar"
                    name="totalSeats"
                    rules={[{ required: true, message: "O'rindiqlar sonini kiriting" }]}
                  >
                    <InputNumber style={{ width: '100%' }} min={1} max={100} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item label="Avtobus raqami" name="busNumber">
                    <Input placeholder="01A123AA" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label="Holat" name="status">
                    <Select
                      options={allowedStatuses.map((s) => ({
                        value: s,
                        label: (
                          <Space>
                            <Tag color={statusColors[s]}>{statusLabels[s] || s}</Tag>
                            {s === trip.status && <span style={{ color: '#999' }}>(joriy)</span>}
                          </Space>
                        ),
                      }))}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item label="Tavsif" name="description">
                <Input.TextArea
                  placeholder="Reys haqida qo'shimcha ma'lumot"
                  rows={3}
                />
              </Form.Item>
            </Form>
          </Card>
        </Col>
      </Row>
    </div>
  );
};
